import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import "./DynamicListGrid.css";
import WaveLoader from '../WaveLoader/WaveLoader'; // Import the LoadingIcon component
import ProductPopup from './ProductPopup'; // path might vary


const ProductList = ({ products, fields, redirect, deleteApi, showEdit = true, showDelete = true, createPath }) => {
  const navigate = useNavigate();
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(false); // Loading state
  const [searchQuery, setSearchQuery] = useState(''); // Search state
  const [currentPage, setCurrentPage] = useState(1); // Current page for pagination
  const [productsPerPage, setProductsPerPage] = useState(30); // Number of products per page (can be changed by user)
  const [totalEntries, setTotalEntries] = useState(0);
  const [dropdownData, setDropdownData] = useState({});
  const [expandedFields, setExpandedFields] = useState({});
  const [expandedText, setExpandedText] = useState({});
  const [selectedRow, setSelectedRow] = useState(null);



const toggleExpand = (id) => {
  setExpandedFields((prev) => ({
    ...prev,
    [id]: !prev[id], // Toggle the expanded state for each row
  }));
};



  useEffect(() => {
    setTotalEntries(products.length); // Set the total entries based on products length
  }, [products]); // Re-run when products change

  useEffect(() => {
    const fetchDropdownData = async () => {
      const newDropdownData = {};

      for (const field of fields) {
        if (field.api) {
          try {
            const response = await axios.get(field.api);
            newDropdownData[field.fields] = response.data.reduce((acc, item) => {
              acc[item._id] = item[field.apiField]; // Map _id → apiField value
              return acc;
            }, {});
          } catch (error) {
            console.error(`Error fetching data for ${field.fields}:`, error);
          }
        }
      }

      setDropdownData(newDropdownData);
    };

    fetchDropdownData();
  }, [fields]);

  const handleCreate = () => {
    if (createPath) {
      navigate(createPath);
    }
  };

  // Handle selecting/deselecting a single product
  const handleSelect = (productId) => {
    if (selectedProducts.includes(productId)) {
      setSelectedProducts(selectedProducts.filter(id => id !== productId));
    } else {
      setSelectedProducts([...selectedProducts, productId]);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await axios.get(`${deleteApi}/download`, {
        responseType: 'blob', // Important for downloading files
      });
  
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'inventory.xlsx'); // Desired filename
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading file:", error);
      alert('Failed to download Excel file.');
    }
  };

  const handleExcelUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
  
    const formData = new FormData();
    formData.append("file", file);
  
    try {
      setLoading(true);
      const response = await axios.post(`${deleteApi}/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
  
      alert(`✅ Uploaded ${response.data.insertedCount} entries successfully.`);
      window.location.reload();
    } catch (error) {
      console.error("Upload error:", error);
      alert("❌ Failed to upload Excel file.");
    } finally {
      setLoading(false);
    }
  };
  
  

  // Handle selecting/deselecting all products
  const handleSelectAll = () => {
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]); // Deselect all
    } else {
      setSelectedProducts(filteredProducts.map(product => product._id)); // Select all
    }
  };

  // Function to handle deleting selected products
  const handleDeleteSelected = async () => {
    if (selectedProducts.length === 0) {
      alert('No products selected for deletion.');
      return;
    }

    const confirmDelete = window.confirm(`Are you sure you want to delete ${selectedProducts.length} products?`);
    if (confirmDelete) {
      setLoading(true); // Start loading
      try {
        await Promise.all(
          selectedProducts.map(productId => axios.delete(`${deleteApi}/${productId}`))
        );
        alert('Selected products deleted successfully!');
        window.location.reload(); // Reload the page or refetch the products
      } catch (error) {
        console.error("Error deleting selected products:", error);
        alert('Failed to delete selected products.');
      } finally {
        setLoading(false); // Stop loading
      }
    }
  };

  // Function to handle editing a product
  const handleEdit = (_id) => {
    const currentUrl = window.location.pathname;
    const redirectUrl = `${currentUrl}/${_id}`;
    navigate(redirectUrl);
  };

  // Function to handle deleting a product
  const handleDelete = async (_id) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this product?");
    if (confirmDelete) {
      setLoading(true); // Start loading
      try {
        await axios.delete(`${deleteApi}/${_id}`);
        alert('Product deleted successfully!');
        window.location.reload(); // Reload the page or refetch the products
      } catch (error) {
        console.error("Error deleting product:", error);
        alert('Failed to delete product.');
      } finally {
        setLoading(false); // Stop loading
      }
    }
  };

  const renderField = (fieldValue, fieldKey, productId) => {
    // Case 1: Use dropdown data if available
    if (dropdownData[fieldKey] && dropdownData[fieldKey][fieldValue]) {
      return dropdownData[fieldKey][fieldValue];
    }
  
    // Case 2: Handle strings with read more
    if (typeof fieldValue === "string") {
      const words = fieldValue.trim().split(/\s+/);
      const isExpanded = expandedText[`${productId}-${fieldKey}`];
  
      if (words.length > 50) {
        const preview = words.slice(0, 50).join(" ");
        return (
          <div>
            <span>{isExpanded ? fieldValue : `${preview}...`}</span>
            <button
              className="read-more-btn"
              onClick={() =>
                setExpandedText(prev => ({
                  ...prev,
                  [`${productId}-${fieldKey}`]: !prev[`${productId}-${fieldKey}`],
                }))
              }
            >
              {isExpanded ? "Read Less" : "Read More"}
            </button>
          </div>
        );
      }
  
      return fieldValue;
    }
  
    // Case 3: Handle arrays
    if (Array.isArray(fieldValue)) {
      return fieldValue
        .map(item => (typeof item === "object" ? item.title || Object.values(item)[1] || JSON.stringify(item) : item))
        .join(", ");
    }
  
    // ✅ Case 4: Handle objects - show 2nd field (excluding _id)
    if (typeof fieldValue === "object" && fieldValue !== null) {
      const entries = Object.entries(fieldValue);
      const secondEntry = entries.find(([key]) => key !== "_id");
      return secondEntry ? secondEntry[1] : JSON.stringify(fieldValue);
    }
  
    // Default fallback
    return fieldValue;
  };
  
  
  
  
  // Limit fields to first 5
  const limitedFields = fields.slice(0, 3);

  // Sort products by _id in ascending order
  const sortedProducts = products.sort((a, b) => (a._id > b._id ? 1 : -1));

  // Convert field names to Title Case
  const formatFieldName = (fieldName) => {
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase());
  };

  // Function to filter products based on search query
  const filteredProducts = sortedProducts.filter(product => {
    return limitedFields.some(field => {
      const fieldValue = product[field.fields];
      return fieldValue && fieldValue.toString().toLowerCase().includes(searchQuery.toLowerCase());
    });
  });

  // Pagination logic
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Handle change in products per page
  const handleProductsPerPageChange = (e) => {
    setProductsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to the first page when changing entries per page
  };

  // Navigate to the admin page
  const navigateToAdmin = () => {
    navigate('/');
  };

  // Show loading indicator if loading
  if (loading) {
    return <WaveLoader />;
  }




  return (
    <div className='DynamicListContainer'>
<div className="header-actions">
  <div className="admin-icon" onClick={navigateToAdmin} title="Go to Admin">
    <FontAwesomeIcon icon={faHome} size="2x" />
  </div>

  {createPath && (
    <button className="create-btn" onClick={handleCreate}>
      <FontAwesomeIcon icon={faPlus} /> Create New Entry
    </button>
  )}

  <button onClick={handleDeleteSelected} className="delete-selected-btn" title="Delete Selected">
    <FontAwesomeIcon icon={faTrash} />
  </button>
</div>

{/* <div className="excel-actions">
  <button onClick={handleDownload} className="download-btn">📥 Download Excel</button>
  <input
    type="file"
    accept=".xlsx, .xls"
    onChange={handleExcelUpload}
    className="upload-input"
  />
</div> */}


<div className="search-container">
  
  <input
    id="search-input"
    type="text"
    placeholder="Type to filter entries..."
    value={searchQuery}
    onChange={(e) => setSearchQuery(e.target.value)}
    className="search-input"
  />
</div>




      {/* Products per page selector */}
      <div className='enteriesPage-container'>
        <label htmlFor="entriesPerPage">Entries per page:</label>
        <select id="entriesPerPage" value={productsPerPage} onChange={handleProductsPerPageChange}>
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>

        {/* Display the total number of entries from the database */}
        <span className="total-entries">
          Total entries: {totalEntries || 'Loading...'}
        </span>
      </div>
      <div style={{ overflowX: 'hidden' }}>
      <table className={`product-table ${fields.length > 6 ? "dynamic-columns" : ""}`}>
  <thead>
  <tr>
      <th>
        <input
          type="checkbox"
          checked={selectedProducts.length === currentProducts.length && currentProducts.length > 0}
          onChange={handleSelectAll}
        />
      </th>
      {limitedFields.map((field) => (
        <th key={field.fields}>{formatFieldName(field.fields)}</th>
      ))}
      {showEdit && <th>Edit</th>}
      {showDelete && <th>Delete</th>}
    </tr>
  </thead>

  <tbody>
    {currentProducts.map((product) => (
       <tr
       key={product._id}
       onClick={() => setSelectedRow(product)}
       style={{ cursor: 'pointer' }}
     >
        <td>
          <input
            type="checkbox"
            checked={selectedProducts.includes(product._id)}
            onChange={() => handleSelect(product._id)}
          />
        </td>
        {limitedFields.map((field) => (
  <td key={field.fields}>
    {field.fields === "photo" ? (
      <img src={product[field.fields]} alt={product.title} style={{ maxWidth: "50px" }} />
    ) : field.api && dropdownData[field.fields] && dropdownData[field.fields][product[field.fields]] ? (
      dropdownData[field.fields][product[field.fields]]
    ) : (
      renderField(product[field.fields], field.fields, product._id)
    )}
  </td>
))}


        {showEdit && (
          <td>
            <button onClick={(e) => { e.stopPropagation(); handleEdit(product._id); }} className="edit-btn">
              Edit
            </button>
          </td>
        )}
        {showDelete && (
          <td>
            <button onClick={(e) => { e.stopPropagation(); handleDelete(product._id); }} className="delete-btn">
              Delete
            </button>
          </td>
        )}
      </tr>
    ))}
  </tbody>
  {currentProducts.length === 0 && (
  <tr>
    <td colSpan={limitedFields.length + (showEdit ? 1 : 0) + (showDelete ? 1 : 0) + 1} className="no-data-cell">
      No data available for the current filter.
    </td>
  </tr>
)}

</table>
</div>


      {/* Pagination Controls */}
      <div className="pagination">
        <button 
          onClick={() => paginate(currentPage - 1)} 
          disabled={currentPage === 1}>
          Previous
        </button>
        {Array.from({ length: Math.ceil(filteredProducts.length / productsPerPage) }, (_, index) => (
          <button key={index + 1} onClick={() => paginate(index + 1)}>
            {index + 1}
          </button>
        ))}
        <button 
          onClick={() => paginate(currentPage + 1)} 
          disabled={currentPage === Math.ceil(filteredProducts.length / productsPerPage)}>
          Next
        </button>
      </div>
      {selectedRow && (
  <ProductPopup
    product={selectedRow}
    fields={fields}
    dropdownData={dropdownData}
    onClose={() => setSelectedRow(null)}
  />
)}

    </div>
    
  );
};

export default ProductList;